import { ColDef } from "ag-grid-enterprise";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import {
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_YYYYMMDD,
  DateFilter
} from "utils/dateutils";
import dayjs from "utils/dayjs-config";
import {
  LastImportedCellRenderer,
  NameCellRenderer,
  OpcImportActionsCellRenderer,
  SrNoCellRenderer
} from "./schedules-in-opc-cell-renders";
import {
  formatImportTimestamp,
  getLastImport,
  getLastImportedByName,
  isScheduleImported,
  TScheduleSourceInfo,
  TScheduleSourceInfoMetadata,
  TScheduleSourceInfoRow
} from "./schedules-in-opc-import-state";

// Re-exported so consumers keep importing the row vocabulary from one place;
// the definitions live in the leaf that the cell renderers also read.
export * from "./schedules-in-opc-import-state";

// Numbered once, in the order the rows are handed over — which is the sorted
// order below, so Sr No. reads 1..n down the grid on load. Deliberately NOT
// derived from node.rowIndex: a display-position counter always reads 1..n no
// matter how the grid is sorted, which makes sorting the column a no-op.
// Stamping the ordinal instead means sorting by Sr No. restores the landing
// order, and the numbers stay attached to their row when another column is
// sorted.
export const withSerialNumbers = (
  rows: Array<TScheduleSourceInfo>
): Array<TScheduleSourceInfoRow> =>
  rows.map((row, index) => ({ ...row, srNo: index + 1 }));

// OPC hands back timestamps like "2026-04-16T00:00:00.+0000" — a stray dot with
// no fractional digits in front of the offset. Neither Date.parse nor dayjs
// accepts that, so the value silently renders blank unless the dot is dropped.
// Only strips a dot sitting directly before the offset/Z, so well-formed
// timestamps ("...T04:01:47.260Z") pass through untouched.
export const normalizeOpcTimestamp = (value?: string | null): string =>
  value ? value.replace(/\.(?=[+-]\d{2}:?\d{2}$|Z$)/, "") : "";

const hasExplicitOffset = (value: string) =>
  /([+-]\d{2}:?\d{2}|Z)$/.test(value);

// Offset-tagged values (dataDate is UTC midnight standing for a calendar date)
// are read in UTC so the day does not roll backwards for projects west of
// Greenwich. Naive values (updateDate carries no zone) are already local
// calendar time and must not be shifted at all.
// The out format is a parameter only because the same value is read twice: the
// grid shows MM-DD-YYYY, and the export payload has to carry YYYY-MM-DD. The
// parsing quirks above are identical either way, so they are not duplicated.
export const formatOpcDate = (
  value?: string | null,
  outFormat: string = DATE_FORMAT_MMDDYYYY
): string => {
  const normalized = normalizeOpcTimestamp(value);
  if (!normalized) return "";

  const parsed = hasExplicitOffset(normalized)
    ? dayjs(normalized).utc()
    : dayjs(normalized);

  return parsed.isValid() ? parsed.format(outFormat) : "";
};

// Which metadata field Date Added reads. Named once so the column and the export
// payload that has to agree with it cannot drift apart.
const DATE_ADDED_METADATA_KEY = "updateDate" as const;

/**
 * Sent to OPC as the date to file the import under, and what ends up in
 * schedule_import_details.schedule_date — the date-side twin of
 * getImportScheduleName, read from exactly the field the Date Added column
 * shows, so the payload says what the user just confirmed against.
 *
 * YYYY-MM-DD rather than the grid's MM-DD-YYYY: that is how every other client
 * write of schedule_date is shaped. undefined rather than "" when OPC sent no
 * updateDate, so the key drops out of the payload instead of handing a date
 * column an empty string.
 */
export const getImportScheduleDate = (
  data?: TScheduleSourceInfo
): string | undefined =>
  formatOpcDate(
    data?.metadata?.[DATE_ADDED_METADATA_KEY],
    DATE_FORMAT_YYYYMMDD
  ) || undefined;

// A comparable instant for Baseline Date. Null when there is nothing to compare,
// so those rows can be pushed to the bottom rather than sorting as epoch zero.
// The instant is what matters here, not the calendar day, so unlike
// formatOpcDate there is no .utc() branch — it would not change the ordering.
export const getBaselineDateValue = (
  data?: TScheduleSourceInfo
): number | null => {
  const normalized = normalizeOpcTimestamp(data?.metadata?.time);
  if (!normalized) return null;
  const parsed = dayjs(normalized);
  return parsed.isValid() ? parsed.valueOf() : null;
};

// Newest baseline first, which is the order the grid lands in. Rows with no
// baseline date go to the bottom — the same treatment comparatorWithEmptyAtBottom
// gives every other column. Copies rather than sorting in place, so the Apollo
// result is never mutated.
export const sortByBaselineDateDesc = (
  rows: Array<TScheduleSourceInfo>
): Array<TScheduleSourceInfo> =>
  [...rows].sort((a, b) => {
    const aValue = getBaselineDateValue(a);
    const bValue = getBaselineDateValue(b);
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;
    return bValue - aValue;
  });

// Returns the displayed text rather than a boolean so that sorting, the set
// filter's checkbox list, and the tooltip all read "Yes"/"No" without a formatter.
export const importedValueGetter = ({
  data
}: {
  data?: TScheduleSourceInfo;
}): string => (isScheduleImported(data) ? "Yes" : "No");

// "MM-DD-YYYY by First Last". Either half can be absent — a schedule imported by
// a since-removed user still has a timestamp worth showing, and a row whose
// import_log has not resolved renders blank rather than "by undefined".
//
// The cell shows these two on separate lines (LastImportedCellRenderer), but the
// value stays one string: it is what the quick-search box matches on, so typing
// either the date or the importer's name still finds the row.
export const lastImportedValueGetter = ({
  data
}: {
  data?: TScheduleSourceInfo;
}): string => {
  const importedOn = formatImportTimestamp(
    getLastImport(data)?.import_log?.created_at
  );
  if (!importedOn) return "";

  const importedBy = getLastImportedByName(data);
  return importedBy ? `${importedOn} by ${importedBy}` : importedOn;
};

// What the FILTER sees for Last Imported, as opposed to what the cell shows.
// The cell reads "MM-DD-YYYY by First Last"; CustomDateSetFilter reparses
// whatever it is handed and cannot make a date out of a name on the end, so the
// column feeds it the bare timestamp instead. ag-grid resolves a filter's value
// through filterValueGetter before valueGetter, which is what this is for.
export const lastImportedFilterValueGetter = ({
  data
}: {
  data?: TScheduleSourceInfo;
}): string =>
  formatImportTimestamp(getLastImport(data)?.import_log?.created_at);

// Sorting has the same problem as filtering: "04-16-2026 by Ada" is text, and as
// text it sorts above "01-02-2027 by Bo". Compares the bare dates off the rows
// themselves — the same trick coldefDateField uses for the submittal log's date
// columns — falling back to the passed values when ag-grid supplies no nodes.
export const lastImportedComparator = (
  valueA: string | null,
  valueB: string | null,
  nodeA?: { data?: TScheduleSourceInfo },
  nodeB?: { data?: TScheduleSourceInfo },
  isInverted?: boolean
): number =>
  DateFilter.comparator(
    nodeA?.data ? lastImportedFilterValueGetter({ data: nodeA.data }) : valueA,
    nodeB?.data ? lastImportedFilterValueGetter({ data: nodeB.data }) : valueB,
    nodeA,
    nodeB,
    isInverted
  );

const metadataValueGetter =
  (key: keyof TScheduleSourceInfoMetadata) =>
  ({ data }: { data?: TScheduleSourceInfo }) =>
    data?.metadata?.[key] ?? "";

const metadataDateValueGetter =
  (key: keyof TScheduleSourceInfoMetadata) =>
  ({ data }: { data?: TScheduleSourceInfo }) =>
    formatOpcDate(data?.metadata?.[key] as string | undefined);

// Every date column across the app — the submittal log, the custom reports, the
// project schedule — filters through this one component: four relative presets
// (Past today / Today / Next 3 days / Next 2 weeks) plus a custom range picker,
// rather than the set filter's checkbox list of every distinct date.
//
// Deliberately no filterParams. Its handler reads the cell through the column's
// own valueGetter and re-parses it with dayjs against DATE_PARSE_FORMATS, which
// leads with MM-DD-YYYY — exactly what formatOpcDate emits — so it needs no
// comparator. The one filterParam it accepts anywhere in the repo is
// `isCustomFilterOnly`, which suppresses the presets; the submittal log passes
// nothing, so neither does this.
const dateColumnFilter = {
  comparator: DateFilter.comparator,
  filter: CustomDateSetFilter
};

// Filtering and its menu tab come from the page's defaultColDef, matching how the
// submittal log configures its string columns — the text columns below simply
// inherit the set filter, the date columns override it with dateColumnFilter as
// the submittal log's do, and only the UI-only columns opt out.
//
// Cell tooltips come from that same defaultColDef, via truncatedTextCellRenderer.
// No column sets tooltipValueGetter: ag-grid disables its whenTruncated gate for
// any column carrying a renderer, so a native tooltip alongside one would fire on
// every hover and stack on top of the renderer's.
//
// Sr No. is pinned left and Imported/Actions pinned right so they stay put while
// the mapped metadata columns scroll between them.
export const getSchedulesInOpcColumnDefs = (): ColDef[] => [
  {
    colId: "sr_no",
    field: "srNo",
    headerName: "Sr No.",
    headerTooltip: "Sr No.",
    // Sortable, so clicking the header puts the rows back in the order OPC
    // returned them. Stays unfiltered — a checkbox list of every ordinal is noise.
    cellRenderer: SrNoCellRenderer,
    pinned: "left",
    width: 80,
    minWidth: 80,
    sortable: true,
    filter: false
  },
  {
    colId: "name",
    headerName: "Name",
    headerTooltip: "Name",
    valueGetter: metadataValueGetter("name"),
    // Active pill sits next to the title (same placement as Schedules).
    cellRenderer: NameCellRenderer,
    minWidth: 220
  },
  {
    colId: "type",
    headerName: "Type",
    headerTooltip: "Type",
    valueGetter: metadataValueGetter("type"),
    minWidth: 120
  },
  {
    colId: "baseline_date",
    headerName: "Baseline Date",
    headerTooltip: "Baseline Date",
    valueGetter: metadataDateValueGetter("time"),
    ...dateColumnFilter,
    // The rows already arrive newest-baseline-first from sortByBaselineDateDesc;
    // this is what puts the matching arrow in the header, so the column stops
    // looking unsorted while the grid is in the order it describes.
    //
    // Safe to let ag-grid own the sort as well: dateColumnFilter supplies
    // DateFilter.comparator, a real date comparison, so this reproduces the
    // incoming order rather than re-sorting the MM-DD-YYYY text. It compares by
    // day where sortByBaselineDateDesc compares by instant, and same-day rows
    // come back 0 — ag-grid's sort is stable, so they keep the finer order the
    // rows arrived in, and empty baselines stay at the bottom either way.
    sort: "desc",
    minWidth: 150
  },
  {
    colId: "date_added",
    headerName: "Date Added",
    headerTooltip: "Date Added",
    valueGetter: metadataDateValueGetter(DATE_ADDED_METADATA_KEY),
    ...dateColumnFilter,
    minWidth: 150
  },
  {
    colId: "status",
    headerName: "Status",
    headerTooltip: "Status",
    valueGetter: metadataValueGetter("status"),
    minWidth: 130
  },
  {
    colId: "category",
    headerName: "Category",
    headerTooltip: "Category",
    valueGetter: metadataValueGetter("baselineCategoryName"),
    minWidth: 180
  },
  {
    colId: "description",
    headerName: "Description",
    headerTooltip: "Description",
    valueGetter: metadataValueGetter("description"),
    minWidth: 220
  },
  {
    colId: "data_date",
    headerName: "Data Date",
    headerTooltip: "Data Date",
    valueGetter: metadataDateValueGetter("dataDate"),
    ...dateColumnFilter,
    minWidth: 140
  },
  {
    colId: "last_imported",
    headerName: "Last Imported",
    headerTooltip: "Last Imported",
    // Shows the date and who did it; filtered and sorted on the date alone, so
    // it behaves like Data Date rather than like a text column that happens to
    // start with a date. Same filter as the three metadata date columns above.
    valueGetter: lastImportedValueGetter,
    filterValueGetter: lastImportedFilterValueGetter,
    comparator: lastImportedComparator,
    filter: CustomDateSetFilter,
    // Date over name, the same two-line cell the submittal and material logs use
    // for their audit columns. Only the layout is its own: the value above is
    // still the one string, so the filter, the sort and the quick search are
    // exactly what they were when this rendered on one line.
    cellRenderer: LastImportedCellRenderer,
    // Pinned with Imported and Actions: the three describe the row's import
    // state and stay together at the frozen right edge while the metadata
    // columns scroll between them.
    //
    // Sized like Data Date now that the renderer stacks the two values. The
    // extra width this used to carry was for "MM-DD-YYYY by First Last" on one
    // line; stacked, the widest thing here is a name, and one long enough to
    // clip gets the same truncation tooltip the other columns give.
    pinned: "right",
    width: 140,
    minWidth: 140
  },
  {
    colId: "imported",
    headerName: "Imported",
    headerTooltip: "Imported",
    valueGetter: importedValueGetter,
    // Now a mapped column, so it inherits sorting and the set filter (a Yes/No
    // checkbox list) from the page's defaultColDef like every other mapped column.
    pinned: "right",
    width: 120,
    maxWidth: 120
  },
  {
    colId: "actions",
    headerName: "Actions",
    headerTooltip: "Actions",
    // One container that picks between import / spinner / imported, rather than
    // a cellRendererSelector: the spinner also reacts to the tracked-import
    // store, and a selector only ever sees ag-grid params.
    //
    // Deliberately left with no `field` and no `valueGetter`. ag-grid
    // force-refreshes a cell that has neither on every row-data change, which is
    // what makes the icon flip the moment a refetch lands; adding a value source
    // as a "change anchor" would switch it back to value diffing and break that.
    cellRenderer: OpcImportActionsCellRenderer,
    pinned: "right",
    width: 120,
    maxWidth: 120,
    sortable: false,
    filter: false,
    menuTabs: []
  }
];
